import { Component, Input, Output, EventEmitter, TemplateRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PaginatorModule } from 'primeng/paginator';

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  cellTemplate?: TemplateRef<any>;
  headerTemplate?: TemplateRef<any>;
  showIcon?: boolean; // Enable icon display for this column
  fixed?: 'left' | 'right'; // Fix column to left or right side
}

/**
 * Returns a new columns array with the given cell templates assigned.
 * Use this instead of mutating column.cellTemplate - mutating does not change
 * the @Input reference, so the table may not re-render (e.g. Action dropdown
 * fails on first load). Immutable update ensures change detection picks it up.
 */
export function withCellTemplates(
  columns: TableColumn[],
  updates: { key: string; cellTemplate: TemplateRef<any> }[]
): TableColumn[] {
  if (!updates.length) return columns;
  return columns.map((col) => {
    const update = updates.find((u) => u.key === col.key);
    return update ? { ...col, cellTemplate: update.cellTemplate } : col;
  });
}

export interface PaginationConfig {
  enabled: boolean;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  pageSizeOptions?: number[];
  serverSide?: boolean; // If true, data is already paginated by server, don't slice
  /** Show the pager even when every row fits on one page. */
  alwaysShow?: boolean;
}

@Component({
  selector: 'app-table',
  standalone: true,
  imports: [CommonModule, PaginatorModule],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent implements OnChanges {
  @Input() columns: TableColumn[] = [];
  /** Initial sort column key (e.g. from URL). When set, table shows this column as sorted. */
  @Input() initialSortColumn: string | null = null;
  /** Initial sort direction. Used when initialSortColumn is set. */
  @Input() initialSortDirection: 'asc' | 'desc' = 'asc';
  @Input() data: any[] = [];
  @Input() loading: boolean = false;
  @Input() skeletonRows: number = 10;
  @Input() pagination: PaginationConfig = {
    enabled: false,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
  };
  @Input() emptyMessage: string | SafeHtml = 'No data available';
  @Input() childRowTemplate?: TemplateRef<any>;
  @Input() isRowExpanded?: (row: any) => boolean;
  /** When set, the row at this index gets higher z-index on fixed-right cell (e.g. for action dropdown). */
  @Input() dropdownOpenRowIndex: number | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  get safeEmptyMessage(): SafeHtml {
    // If emptyMessage is already SafeHtml, return it directly
    if (this.emptyMessage && typeof this.emptyMessage !== 'string') {
      return this.emptyMessage as SafeHtml;
    }
    
    // If it's a string, process it for activities highlighting
    let message = this.emptyMessage as string;
    if (message && (message.includes('activities') || message.includes('Activities'))) {
      message = message.replace(
        /(activities|Activities)/gi,
        '<span class="empty-message-highlight">$1</span>'
      );
    }
    return this.sanitizer.bypassSecurityTrustHtml(message);
  }
  @Input() striped: boolean = false;
  @Input() bordered: boolean = true;
  @Input() hoverable: boolean = true;
  @Input() scrollable: boolean = false;
  @Input() scrollHeight: string = '400px';
  /** When true, shows a pill Export button above the table (top-right). */
  @Input() showExportButton: boolean = false;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() sortChange = new EventEmitter<{ column: string; direction: 'asc' | 'desc' }>();
  @Output() export = new EventEmitter<void>();

  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialSortColumn'] || changes['initialSortDirection']) {
      if (this.initialSortColumn != null) {
        this.sortColumn = this.initialSortColumn;
        this.sortDirection = this.initialSortDirection;
      } else {
        this.sortColumn = null;
        this.sortDirection = 'asc';
      }
    }
  }

  get totalPages(): number {
    if (!this.pagination.enabled) return 0;
    return Math.ceil(this.pagination.totalItems / this.pagination.pageSize);
  }

  get displayedData(): any[] {
    if (!this.pagination.enabled) {
      return this.data;
    }

    // If server-side pagination, return data as-is (already paginated by server)
    if (this.pagination.serverSide) {
      return this.data;
    }

    // Client-side pagination: slice the data array
    const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
    const end = start + this.pagination.pageSize;
    return this.data.slice(start, end);
  }

  get startItem(): number {
    if (!this.pagination.enabled || this.pagination.totalItems === 0) return 0;
    return (this.pagination.currentPage - 1) * this.pagination.pageSize + 1;
  }

  get endItem(): number {
    if (!this.pagination.enabled) return this.data.length;
    const end = this.pagination.currentPage * this.pagination.pageSize;
    return Math.min(end, this.pagination.totalItems);
  }

  /** True when any column has fixed: 'right' (sticky Action column). */
  get hasFixedRightColumn(): boolean {
    return this.columns.some((c) => c.fixed === 'right');
  }

  /** True when any column has fixed: 'left' (e.g. sticky expand chevron). */
  get hasFixedLeftColumn(): boolean {
    return this.columns.some((c) => c.fixed === 'left');
  }

  get shouldShowPagination(): boolean {
    if (!this.pagination.enabled) {
      return false;
    }
    const total = this.pagination.totalItems ?? 0;
    const pageSize = this.pagination.pageSize ?? 10;
    if (total <= 0) return false;
    if (this.pagination.alwaysShow) return true;
    return total > pageSize;
  }

  onPageChange(event: any): void {
    // PrimeNG paginator event structure: { first, rows, page, pageCount }
    const newPage = (event.first / event.rows) + 1;
    const prevPageSize = this.pagination.pageSize;

    // Update pagination config
    this.pagination = {
      ...this.pagination,
      currentPage: newPage,
      pageSize: event.rows,
    };

    // Emit events for backward compatibility
    this.pageChange.emit(newPage);
    if (event.rows !== prevPageSize) {
      this.pageSizeChange.emit(event.rows);
    }
  }

  onSort(column: TableColumn): void {
    if (!column.sortable) return;

    if (this.sortColumn === column.key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column.key;
      this.sortDirection = 'asc';
    }

    this.sortChange.emit({
      column: column.key,
      direction: this.sortDirection,
    });
  }

  getSortIcon(column: TableColumn): string {
    if (this.sortColumn !== column.key) {
      return '⇅';
    }
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  getCellValue(row: any, column: TableColumn): any {
    return row[column.key];
  }

  getSkeletonRows(): number[] {
    return Array.from({ length: this.skeletonRows }, (_, i) => i);
  }

  getAssetIcon(assetType: string | string[] | null | undefined): string {
    // Normalize: API may return type as array (e.g. ["adex:AiModel"])
    const raw = assetType == null ? '' : Array.isArray(assetType) ? (assetType[0] ?? '') : assetType;
    const type = typeof raw === 'string' ? raw.toLowerCase() : '';
    
    if (type.includes('dataset') || type.includes('data') || type.includes('databank')) {
      return '/asset-dataBank.svg';
    } else if (type.includes('model') || type.includes('ai')) {
      return '/asset-ai-model.svg';
    } else if (type.includes('use case') || type.includes('usecase') || type.includes('app')) {
      return '/asset-use-case.svg';
    } else if (type.includes('challenge')) {
      return '/asset-use-case.svg'; // Using use-case icon for challenges
    }
    
    return '';
  }

  shouldShowIcon(column: TableColumn): boolean {
    return column.showIcon === true || 
           column.key.toLowerCase().includes('type') ||
           column.label.toLowerCase().includes('type');
  }

  // Split date and time for formatting (handles "DD-MMM-YYYY HH:mm" and ISO "YYYY-MM-DDTHH:mm:ss")
  getDatePart(value: any): string {
    if (!value) return '';
    const stringValue = typeof value === 'string' ? value : String(value);
    // ISO format: 2026-02-02T14:30:00.000Z
    if (stringValue.includes('T')) {
      return stringValue.split('T')[0] || stringValue;
    }
    const parts = stringValue.split(' ');
    return parts[0] || stringValue;
  }

  getTimePart(value: any): string {
    if (!value) return '';
    const stringValue = typeof value === 'string' ? value : String(value);
    // ISO format: take part after 'T' and strip trailing Z and milliseconds for display
    if (stringValue.includes('T')) {
      const timePart = stringValue.split('T')[1] || '';
      return timePart.replace(/\.\d+Z?$/i, '').replace(/Z$/i, '') || '';
    }
    const parts = stringValue.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : '';
  }

  isDateColumn(column: TableColumn): boolean {
    const key = column.key.toLowerCase();
    const label = column.label.toLowerCase();
    const dateKey =
      key.includes('date') ||
      key.includes('expiry') ||
      key.includes('expires') ||
      /(?:created|updated|added|modified|deleted)(?:at|on)$/.test(key);
    const dateLabel =
      /\bdate\b/.test(label) ||
      /\baccessed\b/.test(label) ||
      /\bexpir(?:y|es)\b/.test(label) ||
      (/\b(created|updated|added|deleted)\b/.test(label) &&
        /\b(date|on|at)\b/.test(label));
    return dateKey || dateLabel;
  }

  /** Only split date/time styling when the cell value looks like a date-time, not plain text. */
  shouldFormatAsDateTime(value: unknown, column: TableColumn): boolean {
    if (!this.isDateColumn(column)) return false;
    const text = value == null ? '' : String(value).trim();
    if (!text) return false;
    if (!/\d/.test(text)) return false;
    if (text.includes('T')) return true;
    if (/\d{4}-\d{2}-\d{2}/.test(text)) return true;
    if (/\d{1,2}-[A-Za-z]{3}-\d{4}/.test(text)) return true;
    if (/\d{1,2}:\d{2}/.test(text)) return true;
    return false;
  }

  onExportClicked(): void {
    this.export.emit();
  }
}



